// The pivot: fields, formula engine, cell arithmetic, and the table itself.

import { useEffect, useMemo, useRef, useState } from 'react';
import { reachApi } from '../api.js';
import { useUnsavedChanges } from '../unsavedChanges.js';
import { T as O } from '../../../shared/styles/tokens.js';
import { ColumnHeaderFilter, DateRangePicker, Field, LoadingState, ResizableHeaderCell, T } from '../components/ui.jsx';
import { factToPivotRow, useCapabilities, useFacts, useAnalysisSource } from '../hooks.js';
import { ANALYSIS_FIELDS, activeFilterIds, formulaFieldAliases, retainedAnalysisFields, selectableAnalysisFields, unavailableFieldIds } from '../fieldCatalog.js';
import { setupFieldChoices } from '../streamSetup.js';
import { DataFilters } from '../components/ExportFilters.jsx';
import { matchesPivotCondition, streamIdForFilter } from '../filterLogic.js';
export { PIVOT_FILTER_OPERATORS, matchesPivotCondition } from '../filterLogic.js';

export const PIVOT_FIELDS = ANALYSIS_FIELDS;


export const FORMULA_FIELD_ALIASES = { date: 'date', account: 'account', ...Object.fromEntries(Object.values(PIVOT_FIELDS).flatMap(field => [
  [field.id.toLowerCase(), field.id],
  [field.label.toLowerCase(), field.id],
])) };


export function tokenizeFormula(formula) {
  const source = String(formula || '').trim().replace(/^=/, '');
  const tokens = [];
  const matcher = /\s*(?:\[([^\]]+)\]|"((?:[^"]|"")*)"|(\d+(?:\.\d+)?)|([A-Za-z_][A-Za-z0-9_.]*)|(>=|<=|<>|!=|[()+\-*/&,=<>]))/gy;
  let index = 0;
  while (index < source.length) {
    matcher.lastIndex = index;
    const match = matcher.exec(source);
    if (!match) throw new Error(`Unexpected value near “${source.slice(index, index + 12)}”.`);
    index = matcher.lastIndex;
    if (match[1] != null) tokens.push({ type: 'field', value: match[1].trim() });
    else if (match[2] != null) tokens.push({ type: 'string', value: match[2].replaceAll('""', '"') });
    else if (match[3] != null) tokens.push({ type: 'number', value: Number(match[3]) });
    else if (match[4] != null) tokens.push({ type: 'identifier', value: match[4] });
    else tokens.push({ type: 'operator', value: match[5] });
  }
  return tokens;
}


export function evaluateExcelFormula(formula, row) {
  const tokens = tokenizeFormula(formula);
  const aliases = formulaFieldAliases(row);
  let position = 0;
  const peek = (value) => tokens[position]?.value === value;
  const take = () => tokens[position++];
  const expect = (value) => { if (!peek(value)) throw new Error(`Expected “${value}”.`); take(); };
  const number = value => { const parsed = Number(value); return Number.isFinite(parsed) ? parsed : 0; };

  const callFunction = (name, args) => {
    const fn = name.toUpperCase();
    if (fn === 'IF') return args[0] ? args[1] : args[2];
    if (fn === 'CONCAT') return args.map(value => String(value ?? '')).join('');
    if (fn === 'LEFT') return String(args[0] ?? '').slice(0, Math.max(0, number(args[1])));
    if (fn === 'RIGHT') return String(args[0] ?? '').slice(-Math.max(0, number(args[1])));
    if (fn === 'MID') return String(args[0] ?? '').slice(Math.max(0, number(args[1]) - 1), Math.max(0, number(args[1]) - 1) + Math.max(0, number(args[2])));
    if (fn === 'UPPER') return String(args[0] ?? '').toUpperCase();
    if (fn === 'LOWER') return String(args[0] ?? '').toLowerCase();
    if (fn === 'TRIM') return String(args[0] ?? '').trim().replace(/\s+/g, ' ');
    if (fn === 'LEN') return String(args[0] ?? '').length;
    if (fn === 'TEXTBEFORE') { const text = String(args[0] ?? ''); const marker = String(args[1] ?? ''); const index = text.indexOf(marker); return index < 0 ? text : text.slice(0, index); }
    if (fn === 'TEXTAFTER') { const text = String(args[0] ?? ''); const marker = String(args[1] ?? ''); const index = text.indexOf(marker); return index < 0 ? '' : text.slice(index + marker.length); }
    if (fn === 'SUBSTITUTE') return String(args[0] ?? '').split(String(args[1] ?? '')).join(String(args[2] ?? ''));
    if (fn === 'FIND') return String(args[1] ?? '').indexOf(String(args[0] ?? ''), Math.max(0, number(args[2] || 1) - 1)) + 1;
    if (fn === 'SEARCH') return String(args[1] ?? '').toLowerCase().indexOf(String(args[0] ?? '').toLowerCase(), Math.max(0, number(args[2] || 1) - 1)) + 1;
    if (fn === 'ROUND') { const places = Math.max(0, number(args[1])); const factor = 10 ** places; return Math.round(number(args[0]) * factor) / factor; }
    if (fn === 'ABS') return Math.abs(number(args[0]));
    if (fn === 'VALUE') return number(args[0]);
    if (fn === 'COALESCE') return args.find(value => value !== '' && value != null) ?? '';
    throw new Error(`Function ${name} is not supported.`);
  };

  const primary = () => {
    const token = take();
    if (!token) throw new Error('Formula is incomplete.');
    if (token.type === 'number' || token.type === 'string') return token.value;
    if (token.type === 'field') {
      const fieldId = aliases[token.value.toLowerCase()];
      if (!fieldId) throw new Error(`Unknown field [${token.value}].`);
      return row[fieldId] ?? '';
    }
    if (token.type === 'identifier') {
      if (token.value.toUpperCase() === 'TRUE') return true;
      if (token.value.toUpperCase() === 'FALSE') return false;
      if (!peek('(')) throw new Error(`Expected “(” after ${token.value}.`);
      take();
      const args = [];
      if (!peek(')')) {
        do { args.push(comparison()); if (!peek(',')) break; take(); } while (position < tokens.length);
      }
      expect(')');
      return callFunction(token.value, args);
    }
    if (token.value === '(') { const value = comparison(); expect(')'); return value; }
    throw new Error(`Unexpected “${token.value}”.`);
  };
  const unary = () => peek('-') ? (take(), -number(unary())) : peek('+') ? (take(), number(unary())) : primary();
  const multiply = () => { let value = unary(); while (peek('*') || peek('/')) { const operator = take().value; const right = unary(); value = operator === '*' ? number(value) * number(right) : number(right) === 0 ? 0 : number(value) / number(right); } return value; };
  const add = () => { let value = multiply(); while (peek('+') || peek('-')) { const operator = take().value; const right = multiply(); value = operator === '+' ? number(value) + number(right) : number(value) - number(right); } return value; };
  const concatenate = () => { let value = add(); while (peek('&')) { take(); value = String(value ?? '') + String(add() ?? ''); } return value; };
  const comparison = () => {
    let value = concatenate();
    while (['=', '<>', '!=', '>', '<', '>=', '<='].includes(tokens[position]?.value)) {
      const operator = take().value; const right = concatenate();
      if (operator === '=') value = value === right;
      else if (operator === '<>' || operator === '!=') value = value !== right;
      else if (operator === '>') value = value > right;
      else if (operator === '<') value = value < right;
      else if (operator === '>=') value = value >= right;
      else value = value <= right;
    }
    return value;
  };
  if (!tokens.length) throw new Error('Enter a formula.');
  const result = comparison();
  if (position < tokens.length) throw new Error(`Unexpected “${tokens[position].value}”.`);
  return result;
}


export function validateCalculatedField(type, formula, sampleRow) {
  try {
    const value = evaluateExcelFormula(formula, sampleRow);
    if (type === 'metric' && !Number.isFinite(Number(value))) return { ok: false, error: 'Calculated metrics must return a number.' };
    return { ok: true, value: type === 'metric' ? Number(value) : String(value ?? '') };
  } catch (error) {
    return { ok: false, error: error.message };
  }
}




export function PivotFieldChip({ field, selected = false, onRemove, onAdd, onDragStart }) {
  return (
    <div
      draggable
      onDragStart={onDragStart}
      style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: 8, borderRadius: O.rSm,
        border: `1px solid ${selected ? O.ruleHover : T.border}`,
        background: selected ? O.surface2 : T.card, color: T.text,
        fontSize: 12, fontWeight: 600, cursor: 'grab', userSelect: 'none',
      }}
    >
      <span aria-hidden="true" style={{ color: T.muted, letterSpacing: -2, marginRight: 2 }}>⋮⋮</span>
      <span style={{ flex: 1 }}>{field.label}</span>
      {selected ? (
        <button
          type="button" className="o-btn o-btn-ghost o-btn-sm" aria-label={`Remove ${field.label}`} onClick={onRemove}
          style={{ border: 'none', background: 'none', color: T.muted, cursor: 'pointer', padding: 0, fontSize: 16, lineHeight: 1 }}
        >×</button>
      ) : (
        <button
          type="button" className="o-btn o-btn-ghost o-btn-sm" aria-label={`Add ${field.label}`} onClick={onAdd}
          style={{ border: 'none', background: 'none', color: T.muted, cursor: 'pointer', padding: 0, fontSize: 16, lineHeight: 1 }}
        >+</button>
      )}
    </div>
  );
}


export function PivotDropZone({ title, hint, type, fieldIds, fieldOptions, fieldMap = PIVOT_FIELDS, onCreateField, onDropField, onRemoveField }) {
  const [isOver, setIsOver] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [fieldSearch, setFieldSearch] = useState('');
  const pickerLabel = type === 'dimension' ? 'dimensions' : 'metrics';
  const matchingFields = fieldOptions.filter(field => field.label.toLowerCase().includes(fieldSearch.trim().toLowerCase()));

  const toggleField = (fieldId) => {
    if (fieldIds.includes(fieldId)) onRemoveField(fieldId, type);
    else onDropField(fieldId, type);
  };

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {title}
        </span>
        <button
          type="button"
          className="o-btn o-btn-ghost o-btn-sm"
          aria-label={`Choose ${pickerLabel}`}
          aria-expanded={pickerOpen}
          onClick={() => { setPickerOpen(open => !open); setFieldSearch(''); }}
          style={{ width: 28, height: 28, padding: 0, justifyContent: 'center', fontSize: 17 }}
        >+</button>
      </div>
      {pickerOpen && (
        <div
          className="o-popover"
          role="dialog"
          aria-label={`Choose ${pickerLabel}`}
          onKeyDown={e => { if (e.key === 'Escape') setPickerOpen(false); }}
          style={{ position: 'absolute', top: 32, left: 0, right: 0, zIndex: 80, padding: 12 }}
        >
          <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <strong style={{ fontSize: 12, color: T.text }}>Choose {pickerLabel}</strong>
            <button
              type="button"
              aria-label={`Close ${pickerLabel} picker`}
              onClick={() => setPickerOpen(false)}
              style={{ width: 28, padding: 4, textAlign: 'center', fontSize: 16, lineHeight: 1 }}
            >×</button>
          </div>
          <input
            className="o-control"
            type="search"
            value={fieldSearch}
            onChange={e => setFieldSearch(e.target.value)}
            placeholder={`Search ${pickerLabel}…`}
            aria-label={`Search ${pickerLabel}`}
            autoFocus
            style={{ marginBottom: 8 }}
          />
          <div style={{ maxHeight: 184, overflowY: 'auto' }}>
            {matchingFields.map(field => {
              const selected = fieldIds.includes(field.id);
              return (
                <button
                  key={field.id}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => toggleField(field.id)}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, background: selected ? O.surface2 : 'none', color: selected ? T.text : T.muted }}
                >
                  <span>{field.label}</span>
                  <span aria-hidden="true" style={{ fontSize: 13 }}>{selected ? '✓' : '+'}</span>
                </button>
              );
            })}
            {matchingFields.length === 0 && (
              <div className="o-note" style={{ padding: 8 }}>No matching {pickerLabel}.</div>
            )}
          </div>
          {onCreateField && <div style={{ paddingTop: 8, marginTop: 8, borderTop: `1px solid ${T.border}` }}><button type="button" className="o-btn o-btn-sm" style={{ width: '100%' }} onClick={() => { setPickerOpen(false); onCreateField(type); }}>+ Create calculated {type}</button></div>}
        </div>
      )}
      <div
        className="o-drop"
        onDragOver={(e) => { e.preventDefault(); setIsOver(true); }}
        onDragLeave={() => setIsOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsOver(false);
          const fieldId = e.dataTransfer.getData('text/plain');
          if (fieldId) onDropField(fieldId, type);
        }}
        style={{
          minHeight: 104, padding: 12, display: 'flex', flexDirection: 'column', gap: 8, textAlign: 'left',
          borderColor: isOver ? O.ruleHover : T.border,
          background: isOver ? O.surface : O.surface2, transition: 'background 120ms, border-color 120ms',
        }}
      >
        {fieldIds.length === 0 && (
          <div style={{ margin: 'auto', padding: 10, textAlign: 'center', fontSize: 12, color: T.muted }}>{hint}</div>
        )}
        {fieldIds.map((fieldId, index) => (
          <div
            key={fieldId}
            onDragOver={e => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsOver(false);
              const droppedId = e.dataTransfer.getData('text/plain');
              if (droppedId) onDropField(droppedId, type, index);
            }}
          >
            <PivotFieldChip
              field={fieldMap[fieldId]}
              selected
              onRemove={() => onRemoveField(fieldId, type)}
              onDragStart={e => e.dataTransfer.setData('text/plain', fieldId)}
            />
          </div>
        ))}
      </div>
    </div>
  );
}


export function PivotFilters({ title = 'Pivot filters', ...props }) {
  return <DataFilters {...props} context="pivot" title={title} />;
}

// --- pivot cell arithmetic --------------------------------------------------
// One accumulator shape for every cell in the grid: a body cell, a row total, a column total
// and the grand total are all the same thing at different scopes, which is what lets the
// totals be re-derived from raw sums instead of by re-aggregating aggregated values — the
// classic way an "average of averages" sneaks into a report.


export const makePivotCell = () => ({ sums: {}, mins: {}, maxs: {}, counts: {}, count: 0, imp: 0, reach: 0, spend: 0, clicks: 0, dates: new Set() });


export function feedPivotCell(cell, row, metricIds) {
  cell.count += 1;
  // Accumulated regardless of which metrics are displayed: frequency and efficiency are
  // ratios of these totals, and averaging per-row ratios would weight a 100-impression day
  // the same as a million-impression day.
  cell.imp += Number(row.impressions || 0);
  cell.reach += Number(row.reach || 0);
  cell.spend += Number(row.spend || 0);
  cell.clicks += Number(row.clicks || 0);
  cell.dates.add(row.dateKey);
  new Set([...metricIds, 'reach', 'impressions', 'spend', 'clicks']).forEach(id => {
    if (row[id] != null && row[id] !== '' && Number.isFinite(Number(row[id]))) cell.counts[id] = (cell.counts[id] || 0) + 1;
  });
  metricIds.forEach(fieldId => {
    if (row[fieldId] == null || row[fieldId] === '' || !Number.isFinite(Number(row[fieldId]))) return;
    const value = Number(row[fieldId]);
    cell.sums[fieldId] = (cell.sums[fieldId] || 0) + value;
    cell.mins[fieldId] = cell.mins[fieldId] == null ? value : Math.min(cell.mins[fieldId], value);
    cell.maxs[fieldId] = cell.maxs[fieldId] == null ? value : Math.max(cell.maxs[fieldId], value);
  });
}


export function mergePivotCell(target, cell, metricIds) {
  target.count += cell.count;
  target.imp += cell.imp;
  target.reach += cell.reach;
  target.spend += cell.spend;
  target.clicks += cell.clicks;
  cell.dates.forEach(dateKey => target.dates.add(dateKey));
  Object.entries(cell.counts).forEach(([id, count]) => { target.counts[id] = (target.counts[id] || 0) + count; });
  metricIds.forEach(fieldId => {
    target.sums[fieldId] = (target.sums[fieldId] || 0) + (cell.sums[fieldId] || 0);
    if (cell.mins[fieldId] != null) target.mins[fieldId] = target.mins[fieldId] == null ? cell.mins[fieldId] : Math.min(target.mins[fieldId], cell.mins[fieldId]);
    if (cell.maxs[fieldId] != null) target.maxs[fieldId] = target.maxs[fieldId] == null ? cell.maxs[fieldId] : Math.max(target.maxs[fieldId], cell.maxs[fieldId]);
  });
}


export function pivotCellValues(cell, metricIds, aggregationFor) {
  return Object.fromEntries(metricIds.map(fieldId => {
    const inputs = { frequency: ['impressions', 'reach'], efficiency: ['reach', 'impressions'], 'metric:cpm': ['spend', 'impressions'], 'metric:cpc': ['spend', 'clicks'], 'metric:ctr': ['clicks', 'impressions'] }[fieldId];
    if (inputs && inputs.some(id => cell.counts[id] !== cell.count)) return [fieldId, null];
    // Ratio metrics are ALWAYS weighted from the cell's own totals — offering "sum" for
    // frequency would resurrect the very bug the weighted computation replaced.
    if (fieldId === 'frequency') return [fieldId, cell.reach > 0 ? cell.imp / cell.reach : null];
    if (fieldId === 'efficiency') return [fieldId, cell.imp > 0 ? cell.reach / cell.imp : null];
    if (fieldId === 'metric:cpm') return [fieldId, cell.imp > 0 ? cell.spend * 1000 / cell.imp : null];
    if (fieldId === 'metric:cpc') return [fieldId, cell.clicks > 0 ? cell.spend / cell.clicks : null];
    if (fieldId === 'metric:ctr') return [fieldId, cell.imp > 0 ? cell.clicks * 100 / cell.imp : null];
    if (!cell.counts[fieldId]) return [fieldId, null];
    const aggregation = aggregationFor(fieldId);
    if (aggregation === 'average') return [fieldId, cell.sums[fieldId] / cell.counts[fieldId]];
    if (aggregation === 'count') return [fieldId, cell.counts[fieldId]];
    if (aggregation === 'min') return [fieldId, cell.mins[fieldId] ?? null];
    if (aggregation === 'max') return [fieldId, cell.maxs[fieldId] ?? null];
    return [fieldId, cell.sums[fieldId] || 0];
  }));
}


// A column key is the JSON of its dimension values; rendered joined for humans.
export const columnKeyLabel = (key) => {
  if (!key) return '—';
  try { return JSON.parse(key).map(value => String(value ?? '—')).join(' · ') || '—'; }
  catch { return String(key); }
};


export const automaticDimensionLabel = (fieldId) => String(fieldId || '')
  .replace(/^breakdown:/, '')
  .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
  .split(/[_\-.\s]+/)
  .filter(Boolean)
  .map(word => word.charAt(0).toUpperCase() + word.slice(1))
  .join(' ');


const granularityForStream = stream => ({ daily: 'day', weekly: 'week', monthly: 'month' }[stream?.reachType] || 'day');


export function PivotTableView({ stream, streams = [stream], calculatedFields = [], onSaveCalculatedField, onSavePivot, onSaved, initialPivot = null, blank = false }) {
  const [selectedStreamIds, setSelectedStreamIds] = useState(() => initialPivot?.sourceStreamIds || [stream.id]);
  const basisStream = streams.find(item => selectedStreamIds.includes(item.id)) || stream;
  const capabilities = useCapabilities(basisStream?.platform);
  const configuredLevels = basisStream?.levels?.length ? basisStream.levels
    : basisStream?.fieldProfile === 'basic' ? setupFieldChoices(capabilities.levels, capabilities.kpis).levels : capabilities.levels;
  const [dimensions, setDimensions] = useState(() => initialPivot?.dimensions || (blank ? [] : ['date', 'account', 'platform']));
  // The pivot follows the selected stream's actual extraction configuration. These used to be
  // manual controls that could request a level or period the stream never extracted and then
  // show a mysterious empty table.
  const granularity = granularityForStream(basisStream);
  const [dimensionFilters, setDimensionFilters] = useState(() => initialPivot?.dimensionFilters || { account: [], platform: [] });
  const [filterOperators, setFilterOperators] = useState(() => initialPivot?.filterOperators || { stream: 'list', account: 'list', platform: 'list' });
  const [filterText, setFilterText] = useState(() => initialPivot?.filterText || { stream: '', account: '', platform: '' });
  const filterIds = activeFilterIds(dimensionFilters, filterOperators).filter(id => id !== 'stream');
  const selectedDetailLevel = [...dimensions, ...filterIds].map(id => PIVOT_FIELDS[id]?.reportingLevel).filter(Boolean)
    .sort((a, b) => ['account', 'campaign', 'adset', 'ad'].indexOf(b) - ['account', 'campaign', 'adset', 'ad'].indexOf(a))[0];
  const level = selectedDetailLevel
    || (dimensions.includes('entity') ? initialPivot?.level : null)
    || (configuredLevels.includes('account') ? 'account' : configuredLevels[0])
    || 'account';
  // Column pivots have been retired; an immutable empty axis keeps legacy table arithmetic
  // on its flat path without preserving a hidden configuration users cannot edit.
  const columnDims = useMemo(() => [], []);
  const [metrics, setMetrics] = useState(() => initialPivot?.metrics || (blank ? [] : ['reach', 'impressions']));
  // Per-metric aggregation overrides (sum/average/min/max/count). Ratio metrics are exempt —
  // they are always weighted from the underlying totals; see pivotCellValues.
  const [aggregations] = useState({});
  // Empty means "all dates" — the server is simply not sent a date filter. This used to be
  // initialised from the loaded rows, which on first render meant from NOTHING (the fetch had
  // not returned), so it was always empty anyway and the min/max hints were blank for good.
  const [dateRange, setDateRange] = useState(() => initialPivot?.dateRange || { from: '', to: '' });
  // Filtered at the source, not in the browser. Fetching every fact in the workspace and
  // filtering client-side burned the server's 5000-row cap on rows the view then threw away —
  // a pivot on one stream could go quietly short because OTHER streams' rows filled the
  // budget. One selected stream, the date range, level and basis all go into the request; the
  // client-side filters below still apply, for the multi-stream case the API cannot express.
  const serverStreamId = streamIdForFilter(selectedStreamIds, filterOperators);
  const analysisStreamIds = (filterOperators.stream || 'list') === 'list' ? selectedStreamIds
    : streams.filter(item => granularityForStream(item) === granularity).map(item => item.id);
  const source = useAnalysisSource({ sourceStreamIds: analysisStreamIds });
  const { loading: factsLoading, facts, error: factsError } = useFacts({
    streamId: serverStreamId,
    streamIds: analysisStreamIds,
    from: dateRange.from || undefined,
    to: dateRange.ongoing ? undefined : dateRange.to || undefined,
    level,
    granularity,
  });
  const streamsById = useMemo(
    () => Object.fromEntries(streams.map(item => [item.id, item])), [streams]);
  const rows = useMemo(() => facts.map(fact => {
    const row = factToPivotRow(fact, streamsById);
    calculatedFields.forEach(field => {
      try { row[field.id] = field.type === 'metric' ? Number(evaluateExcelFormula(field.formula, row)) : String(evaluateExcelFormula(field.formula, row) ?? ''); }
      catch { row[field.id] = field.type === 'metric' ? null : ''; }
    });
    return row;
  }), [facts, streamsById, calculatedFields]);
  const availableFields = useMemo(() => selectableAnalysisFields(source.fields, calculatedFields), [source.fields, calculatedFields]);
  const selectedFieldIds = [...dimensions, ...metrics, ...filterIds];
  const pivotFields = retainedAnalysisFields(availableFields, selectedFieldIds, initialPivot?.fieldDefinitions);
  const unavailableFields = unavailableFieldIds(availableFields, selectedFieldIds);
  const analysisReady = !source.loading && !source.error && !factsLoading && !factsError && !unavailableFields.length && analysisStreamIds.length > 0;
  // The server stops at 5000 rows without saying so in the payload — this is how the view
  // says so. Silent truncation on an analysis screen is how a short number reaches a report.
  const atRowCap = facts.length >= 5000;
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveBusy, setSaveBusy] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [pivotName, setPivotName] = useState(() => initialPivot?.name || (blank ? 'Untitled pivot' : `${stream.name} pivot`));
  const [sort, setSort] = useState(() => initialPivot?.sort || { field: '', direction: 'asc' });
  const showTotals = true;
  const percentOfTotal = [];
  const [calculatedFieldType, setCalculatedFieldType] = useState(null);
  const [headerFilters, setHeaderFilters] = useState({});
  const [pivotColumnWidths, setPivotColumnWidths] = useState({});
  const dimensionFields = Object.values(availableFields).filter(field => field.type === 'dimension');
  const filterableDimensionFields = Object.values(retainedAnalysisFields(availableFields, filterIds)).filter(field => field.type === 'dimension');
  const metricFields = Object.values(availableFields).filter(field => field.type === 'metric');

  const zoneSetters = { rows: setDimensions, metric: setMetrics };
  const placeField = (zone) => (fieldId, fieldType, targetIndex) => {
    const field = pivotFields[fieldId];
    const wantType = zone === 'metric' ? 'metric' : 'dimension';
    if (!field || field.type !== wantType) return;
    zoneSetters[zone](current => {
      const reportingLevel = field.reportingLevel;
      const next = current.filter(id => id !== fieldId
        && !(zone === 'rows' && reportingLevel && PIVOT_FIELDS[id]?.reportingLevel
          && PIVOT_FIELDS[id].reportingLevel !== reportingLevel));
      const index = targetIndex == null ? next.length : Math.min(targetIndex, next.length);
      next.splice(index, 0, fieldId);
      return next;
    });
  };
  const removeFromZone = (zone) => (fieldId) =>
    zoneSetters[zone](current => current.filter(id => id !== fieldId));

  const filteredRows = useMemo(() => rows.filter(row => {
    const streamOperator = filterOperators.stream || 'list';
    const streamMatches = streamOperator === 'list'
      ? selectedStreamIds.includes(row.streamId)
      : matchesPivotCondition(row.stream, streamOperator, filterText.stream || '');
    const dimensionsMatch = filterIds.every(fieldId => {
        const operator = filterOperators[fieldId] || 'list';
        const selected = dimensionFilters[fieldId] || [];
        return operator === 'list'
          ? selected.length === 0 || selected.map(String).includes(String(row[fieldId] ?? ''))
          : matchesPivotCondition(row[fieldId], operator, filterText[fieldId] || '');
      });
    return streamMatches
      && dimensionsMatch
      && (!dateRange.from || row.dateKey >= dateRange.from)
      && (!dateRange.to || row.dateKey <= dateRange.to);
  }), [rows, dateRange, selectedStreamIds, dimensionFilters, filterOperators, filterText, filterableDimensionFields]);

  const aggregationFor = fieldId => (fieldId === 'frequency' || fieldId === 'efficiency')
    ? 'weighted'
    : aggregations[fieldId] || pivotFields[fieldId]?.defaultAggregation
      || (pivotFields[fieldId]?.calculated ? 'average' : 'sum');

  // Every observed combination of the column dimensions, sorted — the matrix's column
  // groups. The single empty key IS flat mode: one column group, rendered without the extra
  // header row, so both modes share one grouping pass instead of two code paths that drift.
  const columnKeys = useMemo(() => {
    if (!columnDims.length) return [''];
    const keys = new Set(filteredRows.map(row => JSON.stringify(columnDims.map(fieldId => row[fieldId]))));
    return [...keys].sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }));
  }, [filteredRows, columnDims]);

  const pivotRows = useMemo(() => {
    const grouped = new Map();
    filteredRows.forEach(row => {
      const rowKey = JSON.stringify(dimensions.map(fieldId => row[fieldId]));
      const colKey = columnDims.length ? JSON.stringify(columnDims.map(fieldId => row[fieldId])) : '';
      if (!grouped.has(rowKey)) {
        grouped.set(rowKey, {
          dimensions: dimensions.map(fieldId => row[fieldId]),
          cellMap: new Map(),
          rowRaw: makePivotCell(),
        });
      }
      const group = grouped.get(rowKey);
      if (!group.cellMap.has(colKey)) group.cellMap.set(colKey, makePivotCell());
      feedPivotCell(group.cellMap.get(colKey), row, metrics);
      feedPivotCell(group.rowRaw, row, metrics);
    });
    const result = [...grouped.values()].map(group => ({
      dimensions: group.dimensions,
      // Body cells keyed by column: raw accumulators kept alongside the computed values so
      // totals can merge raw and re-derive, never average averages.
      cells: Object.fromEntries([...group.cellMap].map(([key, cell]) => [key, {
        ...cell,
        values: pivotCellValues(cell, metrics, aggregationFor),
        // Distinct dates folded into this cell. At day granularity a reach cell spanning
        // several dates is an over-count (repeat viewers counted once per day) and is
        // marked, not presented as measured.
        dateSpan: cell.dates.size,
      }])),
      raw: group.rowRaw,
      values: pivotCellValues(group.rowRaw, metrics, aggregationFor),
      dateSpan: group.rowRaw.dates.size,
      imp: group.rowRaw.imp,
      reach: group.rowRaw.reach,
    }));
    if (!sort.field) return result;
    const sortIndex = dimensions.indexOf(sort.field);
    return result.sort((a, b) => {
      const left = sortIndex >= 0 ? a.dimensions[sortIndex] : a.values[sort.field];
      const right = sortIndex >= 0 ? b.dimensions[sortIndex] : b.values[sort.field];
      return String(left).localeCompare(String(right), undefined, { numeric: true }) * (sort.direction === 'asc' ? 1 : -1);
    });
    // aggregationFor is rebuilt per render; its inputs (aggregations, pivotFields) are what
    // actually change the output and are listed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredRows, dimensions, columnDims, metrics, sort, pivotFields, aggregations]);

  const visiblePivotRows = useMemo(() => pivotRows.filter(row => {
    // Header filters belong to the flat table, whose headers carry the filter controls. In
    // matrix mode those controls do not render, so a filter typed earlier would keep hiding
    // rows with nothing on screen to say so or clear it — ignored rather than half-applied.
    if (columnDims.length > 0) return true;
    const dimensionMatches = dimensions.every((fieldId, index) => !headerFilters[fieldId]
      || String(row.dimensions[index] ?? '').toLowerCase().includes(String(headerFilters[fieldId]).toLowerCase()));
    const metricMatches = metrics.every(fieldId => !headerFilters[fieldId]
      || String(row.values[fieldId] ?? '').toLowerCase().includes(String(headerFilters[fieldId]).toLowerCase()));
    return dimensionMatches && metricMatches;
  }), [pivotRows, dimensions, columnDims, metrics, headerFilters]);
  const setHeaderFilter = fieldId => value => setHeaderFilters(current => ({ ...current, [fieldId]: value }));
  const pivotColumnWidth = fieldId => pivotColumnWidths[fieldId] || 160;
  const resizePivotColumn = fieldId => width => setPivotColumnWidths(current => ({ ...current, [fieldId]: width }));

  const [bounds, setBounds] = useState({ from: '', to: '' });
  const boundsKey = JSON.stringify([selectedStreamIds, level, granularity]);
  useEffect(() => {
    let active = true;
    reachApi.factBounds({ streams: selectedStreamIds, level, granularity }).then(result => { if (active) setBounds(result); }).catch(() => { if (active) setBounds({ from: '', to: '' }); });
    return () => { active = false; };
  }, [boundsKey, facts]);
  const minDate = bounds.from;
  const maxDate = bounds.to;
  const configurationKey = JSON.stringify([pivotName, dimensions, metrics, selectedStreamIds, dimensionFilters, filterOperators, filterText, { from: dateRange.from, to: dateRange.ongoing ? '' : dateRange.to, ongoing: dateRange.ongoing }, sort]);
  const originalConfiguration = useRef(configurationKey);
  const markSaved = useUnsavedChanges(configurationKey !== originalConfiguration.current, configurationKey);
  const cellStyle = { padding: '9px 12px', fontSize: 12, color: T.text, borderBottom: `1px solid ${T.border}`, whiteSpace: 'nowrap' };
  const headStyle = { ...cellStyle, background: O.surface2, color: T.muted, fontWeight: 700, textAlign: 'left', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: 10 };
  // '—' for null/NaN rather than crashing into 'NaN': a frequency with no reach behind it is
  // deliberately null upstream, and rendering it as a number would invent a figure.
  const formatValue = (fieldId, value) => (value == null || Number.isNaN(Number(value)))
    ? '—'
    : fieldId === 'frequency' ? Number(value).toFixed(2)
    : fieldId === 'efficiency' ? `${(value * 100).toFixed(1)}%`
    : fieldId === 'metric:ctr' ? `${Number(value).toFixed(2)}%`
    : ['spend', 'metric:cpm', 'metric:cpc'].includes(fieldId) ? Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : Number(value).toLocaleString(undefined, { maximumFractionDigits: 4 });
  // Totals merge the RAW accumulators of the visible rows and re-derive — computed off
  // sums, never off the already-aggregated cell values, or an averaged metric's total would
  // be an average of averages and a min's total would be nonsense.
  const totalsByColumn = useMemo(() => {
    const out = {};
    for (const key of columnKeys) {
      const merged = makePivotCell();
      visiblePivotRows.forEach(row => { if (row.cells[key]) mergePivotCell(merged, row.cells[key], metrics); });
      out[key] = { values: pivotCellValues(merged, metrics, aggregationFor), dateSpan: merged.dates.size, count: merged.count };
    }
    const grand = makePivotCell();
    visiblePivotRows.forEach(row => mergePivotCell(grand, row.raw, metrics));
    out._grand = { values: pivotCellValues(grand, metrics, aggregationFor), dateSpan: grand.dates.size, count: grand.count };
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visiblePivotRows, columnKeys, metrics, aggregations, pivotFields]);
  const metricTotals = totalsByColumn._grand.values;
  // Reach summed across days at day granularity counts repeat viewers once per day. Marked
  // rather than hidden: the number is still what every daily-data tool shows, but here the
  // honest one is a granularity switch away, so the table says which kind it is showing.
  const reachIsOvercount = (cell) => (cell?.dateSpan || 0) > 1 || (cell?.count || cell?.raw?.count || 0) > 1 || level !== 'account' || selectedStreamIds.length > 1;
  const totalsReachOvercount = reachIsOvercount(totalsByColumn._grand);
  const showReachFootnote = metrics.includes('reach')
    && (totalsReachOvercount || visiblePivotRows.some(reachIsOvercount));
  const exportCsv = () => {
    if (atRowCap || !analysisReady) return;
    const label = id => pivotFields[id]?.label || id;
    // With a column axis, the CSV mirrors the matrix: one column per (column group, metric),
    // plus the row totals — flat exports keep their original shape untouched.
    const headers = columnDims.length
      ? [...dimensions.map(label),
         ...columnKeys.flatMap(key => metrics.map(id => `${columnKeyLabel(key)} — ${label(id)}`)),
         ...metrics.map(id => `Total ${label(id)}`)]
      : [...dimensions, ...metrics].map(label);
    const csvRows = visiblePivotRows.map(row => columnDims.length
      ? [...row.dimensions,
         ...columnKeys.flatMap(key => metrics.map(id => row.cells[key]?.values[id] ?? '')),
         ...metrics.map(id => row.values[id] ?? '')]
      : [...row.dimensions, ...metrics.map(id => row.values[id] ?? '')]);
    const csv = [headers, ...csvRows].map(row => row.map(value => `"${String(value).replaceAll('"', '""')}"`).join(',')).join('\n');
    const link = document.createElement('a');
    link.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    link.download = `${pivotName.trim().replaceAll(' ', '-').toLowerCase() || 'reach-pivot'}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };
  const selectedStream = streams.find(item => selectedStreamIds.length === 1 && selectedStreamIds.includes(item.id));
  const pivotTitle = selectedStream?.name || (selectedStreamIds.length ? `${selectedStreamIds.length} streams` : 'No streams selected');
  const displayTitle = initialPivot?.name || pivotTitle;

  const savePivot = async () => {
    const name = pivotName.trim();
    if (!name || saveBusy || !analysisReady) return;
    setSaveBusy(true); setSaveError('');
    try {
    await onSavePivot({
      id: initialPivot?.id,
      name,
      sourceStreamIds: [...selectedStreamIds],
      dimensions: [...dimensions],
      columns: [...columnDims],
      metrics: [...metrics],
      aggregations: { ...aggregations },
      fieldDefinitions: Object.fromEntries(
        [...dimensions, ...columnDims]
          .filter(id => pivotFields[id]?.automatic)
          .map(id => [id, pivotFields[id]]),
      ),
      // The basis and level are part of what the numbers MEAN — a monthly-reach pivot
      // reopened at daily basis would show different figures under the same name.
      granularity,
      level,
      dateRange: { ...dateRange },
      dimensionFilters: Object.fromEntries(Object.entries(dimensionFilters).map(([id, values]) => [id, [...values]])),
      filterOperators: { ...filterOperators },
      filterText: { ...filterText },
      sort: { ...sort },
      showTotals,
      percentOfTotal: [...percentOfTotal],
      rowCount: atRowCap ? null : filteredRows.length,
    });
    originalConfiguration.current = configurationKey;
    markSaved(configurationKey);
    setSaveDialogOpen(false);
    onSaved?.();
    } catch (error) { setSaveError(error.message || 'The pivot could not be saved. Your changes are still here.'); }
    finally { setSaveBusy(false); }
  };

  return (
    <div className="reach-pivot-layout" style={{ display: 'grid', gridTemplateColumns: 'minmax(240px, 280px) minmax(0, 1fr)', gap: 18, alignItems: 'start' }}>
      <aside className="o-panel" style={{ padding: 16 }}>
        <div style={{ marginBottom: 4, fontSize: 13, fontWeight: 700, color: T.text }}>Build pivot</div>
        <div style={{ marginBottom: 16, fontSize: 11, color: T.muted }}>Fields are read automatically from the selected stream&apos;s extracted data.</div>
        {source.loading && <p className="o-note" role="status">Reading available fields…</p>}
        {source.error && <div className="o-state o-error" role="alert">{source.error}<button type="button" className="o-btn o-btn-sm" onClick={source.retry}>Try again</button></div>}
        {!source.loading && !source.error && unavailableFields.length > 0 && <div className="o-hint" role="alert"><b>Review these fields:</b> {unavailableFields.map(id => pivotFields[id].label).join(', ')}. They are no longer selectable or not available in these streams. Remove them or select a stream containing them before saving or exporting. Saved configurations are not changed automatically.</div>}
        {!basisStream?.levels?.length && capabilities.loading && <p className="o-note" role="status">Loading available reporting dimensions…</p>}
        {!basisStream?.levels?.length && !capabilities.loading && (capabilities.error || !capabilities.known) && <div className="o-hint" role="alert">
          <b>Reporting dimensions could not be loaded.</b> {capabilities.error || 'No extractor is registered for this platform.'}
          <p className="o-note">Existing data remains visible. Retry to choose another reporting level.</p>
          <button type="button" className="o-btn o-btn-sm" onClick={capabilities.retry}>Try again</button>
        </div>}

        <div style={{ paddingBottom: 15, marginBottom: 15, borderBottom: `1px solid ${T.border}` }}>
          <div style={{ marginBottom: 8, fontSize: 11, fontWeight: 700, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Date range</div>
          <DateRangePicker label="Pivot date range" value={dateRange} onChange={setDateRange} min={minDate} max={maxDate} emptyLabel="All dates" />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
          <PivotDropZone
            title="Rows · Dimensions" hint="Drop dimensions here" type="dimension"
            fieldIds={dimensions} fieldOptions={dimensionFields}
            fieldMap={pivotFields} onCreateField={onSaveCalculatedField ? setCalculatedFieldType : undefined}
            onDropField={placeField('rows')} onRemoveField={removeFromZone('rows')}
          />
          <PivotDropZone
            title="Values · Metrics" hint="Drop metrics here" type="metric"
            fieldIds={metrics} fieldOptions={metricFields}
            fieldMap={pivotFields} onCreateField={onSaveCalculatedField ? setCalculatedFieldType : undefined}
            onDropField={placeField('metric')} onRemoveField={removeFromZone('metric')}
          />
          <PivotFilters
            loadValues={(field, search, after, sourceStreamIds) => reachApi.exportValues({ field, search, after, sourceStreamIds })}
            rows={rows}
            streams={streams}
            dimensionFields={filterableDimensionFields}
            selectedStreamIds={selectedStreamIds}
            setSelectedStreamIds={setSelectedStreamIds}
            dimensionFilters={dimensionFilters}
            setDimensionFilters={setDimensionFilters}
            filterOperators={filterOperators}
            setFilterOperators={setFilterOperators}
            filterText={filterText}
            setFilterText={setFilterText}
          />
        </div>
      </aside>

      <div style={{ minWidth: 0 }}>
        <div className="reach-pivot-head" style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'end', marginBottom: 16 }}>
          <div>
            <h2 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 700, color: T.text }}>
              Pivot table — {displayTitle}
            </h2>
            <p style={{ margin: 0, fontSize: 13, color: T.muted }}>Your table updates as you build it.</p>
          </div>
          <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 12, color: T.muted }}>
              {filteredRows.length} source rows · {dimensions.length + metrics.length} columns
            </span>
            <button type="button" className="o-btn" disabled={!analysisReady || atRowCap || pivotRows.length === 0} title={atRowCap ? 'Narrow the selection before downloading a complete CSV.' : undefined} onClick={exportCsv}>Export CSV</button>
            <button
              type="button"
              className="o-btn o-btn-primary"
              disabled={!analysisReady || dimensions.length + metrics.length === 0}
              onClick={() => {
                setSaveDialogOpen(true);
              }}
            >
              Save pivot table
            </button>
          </div>
        </div>

        {/* Failure, loading and empty are three different answers to "where are my numbers",
            and the table used to give all three the same one — "no data matches your filters"
            — while both state flags sat computed and unread. */}
        {factsError && (
          <div className="o-state o-error" style={{ marginBottom: 12 }}>
            <b>Extracted data could not be read.</b> {factsError}
          </div>
        )}
        {atRowCap && (
          <div className="o-hint" style={{ marginBottom: 12 }}>
            <b>Showing the first 5,000 rows.</b> The result hit the server&apos;s cap, so totals
            below are partial, and CSV download is disabled. Narrow the date range, pick a single
            stream, or filter to a lower level to bring the full result under the cap.
          </div>
        )}
        <div className="o-panel" style={{ overflowX: 'auto' }}>
          {factsLoading ? (
            <LoadingState title="Loading pivot data…" detail="Applying the selected streams, date range and filters." style={{ maxWidth: 'none', border: 0 }} />
          ) : dimensions.length + metrics.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: T.muted, fontSize: 13 }}>Add at least one field to display the pivot table.</div>
          ) : columnDims.length > 0 ? (
            /* The matrix: column dimensions pivoted across the top, a metric sub-column per
               group, row totals on the right. Plain headers by design — per-cell filters and
               resizing belong to the flat table, where a column IS one field; here a column
               is one field value, and filtering those is what the sidebar filters do. */
            <table className="o-tbl" style={{ width: '100%', minWidth: Math.max(640, dimensions.length * 140 + (columnKeys.length + 1) * metrics.length * 110), borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {dimensions.map(fieldId => (
                    <th key={fieldId} rowSpan={2} style={{ ...headStyle, verticalAlign: 'bottom', cursor: 'pointer' }}
                        onClick={() => setSort(current => ({ field: fieldId, direction: current.field === fieldId && current.direction === 'asc' ? 'desc' : 'asc' }))}>
                      {pivotFields[fieldId]?.label || fieldId}{sort.field === fieldId ? (sort.direction === 'asc' ? ' ↑' : ' ↓') : ''}
                    </th>
                  ))}
                  {columnKeys.map(key => (
                    <th key={key} colSpan={metrics.length} style={{ ...headStyle, textAlign: 'center', borderLeft: `1px solid ${T.border}` }}>{columnKeyLabel(key)}</th>
                  ))}
                  <th colSpan={metrics.length} style={{ ...headStyle, textAlign: 'center', borderLeft: `1px solid ${T.border}` }}>Total</th>
                </tr>
                <tr>
                  {columnKeys.map(key => metrics.map((fieldId, metricIndex) => (
                    <th key={`${key}:${fieldId}`} style={{ ...headStyle, textAlign: 'right', ...(metricIndex === 0 ? { borderLeft: `1px solid ${T.border}` } : {}) }}>{pivotFields[fieldId]?.label || fieldId}</th>
                  )))}
                  {metrics.map((fieldId, metricIndex) => (
                    <th key={`total:${fieldId}`} style={{ ...headStyle, textAlign: 'right', ...(metricIndex === 0 ? { borderLeft: `1px solid ${T.border}` } : {}) }}>{pivotFields[fieldId]?.label || fieldId}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visiblePivotRows.map((row, index) => (
                  <tr key={index}>
                    {row.dimensions.map((value, dimensionIndex) => <td key={dimensions[dimensionIndex]} style={cellStyle}>{value}</td>)}
                    {columnKeys.map(key => metrics.map((fieldId, metricIndex) => {
                      const cell = row.cells[key];
                      return (
                        <td key={`${key}:${fieldId}`} className="o-num" style={{ ...cellStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums', ...(metricIndex === 0 ? { borderLeft: `1px solid ${T.border}` } : {}) }}>
                          {cell ? formatValue(fieldId, cell.values[fieldId]) : '—'}{fieldId === 'reach' && cell && reachIsOvercount(cell) ? '†' : ''}
                        </td>
                      );
                    }))}
                    {metrics.map((fieldId, metricIndex) => (
                      <td key={`total:${fieldId}`} className="o-num o-strong" style={{ ...cellStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums', ...(metricIndex === 0 ? { borderLeft: `1px solid ${T.border}` } : {}) }}>
                        {formatValue(fieldId, row.values[fieldId])}{fieldId === 'reach' && reachIsOvercount(row) ? '†' : ''}
                      </td>
                    ))}
                  </tr>
                ))}
                {showTotals && visiblePivotRows.length > 0 && (
                  <tr>
                    {dimensions.map((fieldId, dimensionIndex) => <td key={fieldId} className="o-strong" style={cellStyle}>{dimensionIndex === 0 ? 'Total' : '—'}</td>)}
                    {columnKeys.map(key => metrics.map((fieldId, metricIndex) => (
                      <td key={`${key}:${fieldId}`} className="o-num o-strong" style={{ ...cellStyle, textAlign: 'right', ...(metricIndex === 0 ? { borderLeft: `1px solid ${T.border}` } : {}) }}>
                        {formatValue(fieldId, totalsByColumn[key]?.values[fieldId])}{fieldId === 'reach' && reachIsOvercount(totalsByColumn[key]) ? '†' : ''}
                      </td>
                    )))}
                    {metrics.map((fieldId, metricIndex) => (
                      <td key={`grand:${fieldId}`} className="o-num o-strong" style={{ ...cellStyle, textAlign: 'right', ...(metricIndex === 0 ? { borderLeft: `1px solid ${T.border}` } : {}) }}>
                        {formatValue(fieldId, metricTotals[fieldId])}{fieldId === 'reach' && totalsReachOvercount ? '†' : ''}
                      </td>
                    ))}
                  </tr>
                )}
                {visiblePivotRows.length === 0 && (
                  <tr><td colSpan={dimensions.length + (columnKeys.length + 1) * metrics.length} style={{ ...cellStyle, padding: 28, textAlign: 'center', color: T.muted }}>No data matches the current date range and column filters.</td></tr>
                )}
              </tbody>
            </table>
          ) : (
            <table className="o-tbl" style={{ width: '100%', minWidth: Math.max(640, [...dimensions, ...metrics].reduce((sum, fieldId) => sum + pivotColumnWidth(fieldId), 0)), borderCollapse: 'collapse', tableLayout: 'fixed' }}>
              <colgroup>{[...dimensions, ...metrics].map(fieldId => <col key={fieldId} style={{ width: pivotColumnWidth(fieldId) }} />)}</colgroup>
              <thead>
                <tr>
                  {dimensions.map(fieldId => <ResizableHeaderCell key={fieldId} width={pivotColumnWidth(fieldId)} onWidthChange={resizePivotColumn(fieldId)} style={headStyle}><ColumnHeaderFilter label={`${pivotFields[fieldId].label}${sort.field === fieldId ? (sort.direction === 'asc' ? ' ↑' : ' ↓') : ''}`} value={headerFilters[fieldId]} onChange={setHeaderFilter(fieldId)} options={[...new Set(pivotRows.map(row => row.dimensions[dimensions.indexOf(fieldId)]))].map(value => ({ value: String(value), label: String(value) }))} onLabelClick={() => setSort(current => ({ field: fieldId, direction: current.field === fieldId && current.direction === 'asc' ? 'desc' : 'asc' }))} /></ResizableHeaderCell>)}
                  {metrics.map(fieldId => <ResizableHeaderCell key={fieldId} width={pivotColumnWidth(fieldId)} onWidthChange={resizePivotColumn(fieldId)} style={{ ...headStyle, textAlign: 'right' }}><ColumnHeaderFilter label={`${pivotFields[fieldId].label}${sort.field === fieldId ? (sort.direction === 'asc' ? ' ↑' : ' ↓') : ''}`} value={headerFilters[fieldId]} onChange={setHeaderFilter(fieldId)} placeholder="Filter numeric values…" onLabelClick={() => setSort(current => ({ field: fieldId, direction: current.field === fieldId && current.direction === 'asc' ? 'desc' : 'asc' }))} /></ResizableHeaderCell>)}
                </tr>
              </thead>
              <tbody>
                {visiblePivotRows.map((row, index) => (
                  <tr key={index}>
                    {row.dimensions.map((value, dimensionIndex) => <td key={dimensions[dimensionIndex]} style={cellStyle}>{value}</td>)}
                    {metrics.map(fieldId => <td className="o-num" key={fieldId} style={{ ...cellStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{percentOfTotal.includes(fieldId) ? `${metricTotals[fieldId] ? (row.values[fieldId] / metricTotals[fieldId] * 100).toFixed(1) : '0.0'}%` : formatValue(fieldId, row.values[fieldId])}{fieldId === 'reach' && reachIsOvercount(row) ? '†' : ''}</td>)}
                  </tr>
                ))}
                {showTotals && visiblePivotRows.length > 0 && <tr>{dimensions.map((fieldId, index) => <td key={fieldId} className="o-strong" style={cellStyle}>{index === 0 ? 'Total' : '—'}</td>)}{metrics.map(fieldId => <td key={fieldId} className="o-num o-strong" style={{ ...cellStyle, textAlign: 'right' }}>{percentOfTotal.includes(fieldId) ? '100.0%' : formatValue(fieldId, metricTotals[fieldId])}{fieldId === 'reach' && totalsReachOvercount ? '†' : ''}</td>)}</tr>}
                {visiblePivotRows.length === 0 && (
                  <tr><td colSpan={dimensions.length + metrics.length} style={{ ...cellStyle, padding: 28, textAlign: 'center', color: T.muted }}>No data matches the current date range and column filters.</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
        {showReachFootnote && (
          <p className="o-note" style={{ margin: '8px 2px 0' }}>
            † Summed reach is not a unique-audience total across periods, entities or streams.
            The same person can appear in several groups. Each period&apos;s reach remains the platform&apos;s reported figure.
          </p>
        )}
      </div>

      {/* Await the save and use the RETURNED field: the server assigns the real id, and
          adding the dialog's throwaway client id to the pivot would reference a field that
          does not exist the moment the list reloads. */}
      {calculatedFieldType && onSaveCalculatedField && <CalculatedFieldDialog type={calculatedFieldType} sourceFields={Object.values(availableFields)} sampleRow={rows[0] || {}} onClose={() => setCalculatedFieldType(null)} onSave={async item => { const saved = await onSaveCalculatedField(item); if (saved) { if (saved.type === 'dimension') setDimensions(current => [...current, saved.id]); else setMetrics(current => [...current, saved.id]); } setCalculatedFieldType(null); }} />}

      {saveDialogOpen && (
        <div className="o-scrim" role="dialog" aria-modal="true" aria-labelledby="save-pivot-title" onClick={() => { if (!saveBusy) setSaveDialogOpen(false); }}>
          <form className="o-modal" onSubmit={event => { event.preventDefault(); savePivot(); }} onClick={event => event.stopPropagation()} style={{ width: 'min(480px, 94vw)' }}>
            <div className="o-modal-head">
              <div>
                <h2 id="save-pivot-title" style={{ margin: 0, fontSize: 18, color: T.text }}>Save pivot table</h2>
                <p className="o-note" style={{ margin: '4px 0 0' }}>Choose a name for this pivot configuration.</p>
              </div>
              <button type="button" className="o-btn o-btn-ghost o-btn-sm" disabled={saveBusy} aria-label="Close save pivot dialog" onClick={() => setSaveDialogOpen(false)}>×</button>
            </div>
            <div className="o-modal-body">
              <label className="o-field-lbl" htmlFor="pivot-table-name">Pivot table name</label>
              <input id="pivot-table-name" className="o-control" disabled={saveBusy} value={pivotName} onChange={event => setPivotName(event.target.value)} style={{ marginTop: 8 }} autoFocus />
              {saveError && <p className="o-hint o-error" role="alert">{saveError}</p>}
            </div>
            <div className="o-modal-foot">
              <button type="button" className="o-btn" disabled={saveBusy} onClick={() => setSaveDialogOpen(false)}>Cancel</button>
              <button type="submit" className="o-btn o-btn-primary" disabled={saveBusy || !pivotName.trim()}>{saveBusy ? 'Saving…' : 'Save pivot table'}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}


export const CALCULATED_FIELD_FUNCTIONS = ['IF', 'CONCAT', 'LEFT', 'RIGHT', 'MID', 'TEXTBEFORE', 'TEXTAFTER', 'SUBSTITUTE', 'FIND', 'SEARCH', 'UPPER', 'LOWER', 'TRIM', 'LEN', 'ROUND', 'ABS', 'VALUE', 'COALESCE'];


export function CalculatedFieldDialog({ type, field = null, sampleRow, sourceFields = [], onClose, onSave }) {
  const [draft, setDraft] = useState(() => field || { name: '', description: '', formula: type === 'metric' ? '=[Reach]/[Impressions]' : '=LEFT([Account], 4)', defaultAggregation: type === 'metric' ? 'average' : undefined });
  const validation = validateCalculatedField(type, draft.formula, sampleRow);
  const insert = value => setDraft(current => ({ ...current, formula: `${current.formula}${value}` }));
  const submit = event => {
    event.preventDefault();
    if (!draft.name.trim() || !validation.ok) return;
    onSave({ ...draft, id: field?.id || `calc_${type}_${Date.now()}`, name: draft.name.trim(), type, calculated: true, updatedAt: new Date().toISOString() });
  };
return <div className="o-scrim" role="dialog" aria-modal="true" aria-labelledby="calculated-field-title" onClick={onClose}><form className="o-modal o-modal-wide" onSubmit={submit} onClick={event => event.stopPropagation()} style={{ width: 'min(820px, 94vw)' }}><div className="o-modal-head"><div><h2 id="calculated-field-title" style={{ margin: 0, fontSize: 18 }}>{field ? 'Edit' : 'Create'} calculated {type}</h2><p className="o-note" style={{ margin: '4px 0 0' }}>Use Excel-style formulas with existing Reach fields.</p></div><button type="button" className="o-btn o-btn-ghost o-btn-sm" aria-label="Close calculated field dialog" onClick={onClose}>×</button></div><div className="o-modal-body" style={{ display: 'grid', gap: 16 }}><div className="reach-form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}><label><span className="o-field-lbl">Field name</span><input className="o-control" value={draft.name} onChange={event => setDraft(current => ({ ...current, name: event.target.value }))} placeholder={type === 'metric' ? 'Reach rate' : 'Account prefix'} style={{ marginTop: 8 }} autoFocus /></label><label><span className="o-field-lbl">Description</span><input className="o-control" value={draft.description || ''} onChange={event => setDraft(current => ({ ...current, description: event.target.value }))} placeholder="What this field represents" style={{ marginTop: 8 }} /></label></div>{type === 'metric' && <label><span className="o-field-lbl">Default aggregation</span><select className="o-control" value={draft.defaultAggregation || 'average'} onChange={event => setDraft(current => ({ ...current, defaultAggregation: event.target.value }))} style={{ marginTop: 8, maxWidth: 260 }}><option value="sum">Sum</option><option value="average">Average</option><option value="count">Count</option></select></label>}<div><label className="o-field-lbl" htmlFor="calculated-field-formula">Formula</label><textarea id="calculated-field-formula" className="o-control o-mono" value={draft.formula} onChange={event => setDraft(current => ({ ...current, formula: event.target.value }))} rows={4} spellCheck="false" style={{ marginTop: 8, width: '100%', resize: 'vertical' }} /></div><div><div className="o-field-lbl" style={{ marginBottom: 8 }}>Insert source field</div><div className="o-chipset">{sourceFields.map(source => <button key={source.id} type="button" className="o-chip" aria-pressed="false" onClick={() => insert(`[${source.id}]`)}>{source.label}</button>)}</div></div><div><div className="o-field-lbl" style={{ marginBottom: 8 }}>Insert function</div><div className="o-chipset">{CALCULATED_FIELD_FUNCTIONS.map(fn => <button key={fn} type="button" className="o-chip" aria-pressed="false" onClick={() => insert(`${fn}(`)}>{fn}</button>)}</div></div><div className={validation.ok ? 'o-hint' : 'o-state o-error'}>{validation.ok ? <><b>Preview result.</b> <span className={type === 'metric' ? 'o-num' : 'o-mono'}>{String(validation.value)}</span></> : <><b>Formula error.</b> {validation.error}</>}</div><div className="o-note">Examples: <span className="o-mono">=MID([Account], 6, 5)</span> · <span className="o-mono">=TEXTBEFORE([Account], "_")</span> · <span className="o-mono">=IF([Platform]="Facebook", "Meta", [Platform])</span> · <span className="o-mono">=ROUND([Reach]/[Impressions], 4)</span></div></div><div className="o-modal-foot"><button type="button" className="o-btn" onClick={onClose}>Cancel</button><button type="submit" className="o-btn o-btn-primary" disabled={!draft.name.trim() || !validation.ok}>Save calculated {type}</button></div></form></div>;
}


export function CalculatedFieldsView({ type, fields, streams, usageByField = {}, onSave, onDelete }) {
  const [editing, setEditing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  // A real extracted row to preview formulas against. Previously an invented one, which meant
  // a formula could be validated against numbers no stream had ever produced — and any field
  // referencing a column this workspace does not have would still look fine.
  const { facts: sampleFacts } = useFacts();
  const sampleStreamsById = useMemo(
    () => Object.fromEntries(streams.map(item => [item.id, item])), [streams]);
  const sampleRow = sampleFacts.length
    ? factToPivotRow(sampleFacts[0], sampleStreamsById)
    : null;
  const title = type === 'metric' ? 'Calculated metrics' : 'Calculated dimensions';
  return <div><div className="o-head-row"><div><h2 style={{ margin: '0 0 4px', fontSize: 18 }}>{title}</h2><p className="o-note" style={{ margin: 0 }}>Create reusable Excel-style fields from existing Reach data.</p></div><button type="button" className="o-btn o-btn-primary" onClick={() => { setEditing(null); setDialogOpen(true); }}>Create calculated {type}</button></div>{fields.length === 0 ? <div className="o-state"><b>No calculated {type}s created.</b> Create one to make it available in pivots and exports.</div> : <div className="o-panel" style={{ overflowX: 'auto' }}><table className="o-tbl" style={{ width: '100%', minWidth: 980 }}><thead><tr><th>Name</th><th>Formula</th><th>Preview</th><th>Used by</th><th>Description</th><th>Actions</th></tr></thead><tbody>{fields.map(item => { const preview = validateCalculatedField(type, item.formula, sampleRow); const usage = usageByField[item.id] || 0; return <tr key={item.id}><td className="o-strong">{item.name}</td><td><span className="o-code">{item.formula}</span></td><td className={type === 'metric' ? 'o-num' : 'o-mono'}>{preview.ok ? String(preview.value) : 'Formula error'}</td><td>{usage ? `${usage} saved asset${usage === 1 ? '' : 's'}` : 'Not used'}</td><td>{item.description || '—'}</td><td className="o-acts"><button type="button" className="o-btn o-btn-sm" onClick={() => { setEditing(item); setDialogOpen(true); }}>Edit</button><button type="button" className="o-btn o-btn-danger o-btn-sm" disabled={usage > 0} title={usage ? 'Remove this field from saved pivots and exports before deleting it.' : undefined} onClick={() => onDelete(item.id)}>Delete</button></td></tr>; })}</tbody></table></div>}{dialogOpen && <CalculatedFieldDialog type={type} field={editing} sampleRow={sampleRow} onClose={() => setDialogOpen(false)} onSave={item => { onSave(item); setDialogOpen(false); }} />}</div>;
}

